import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/app/_module/utils/show-toast";

import {
  getAgencyRouteSettings,
  updateAgencyRouteSettings,
  getAgencyRouteStats,
  getAgencyLiveAgents,
  getAgencyRouteHistory,
  getStatsInclusion,
  updateStatsInclusion,
  type StatsInclusionSettings,
  type RouteHistoryFilters,
} from "../services/agency-routes";

type AgencyRouteSettings = Awaited<ReturnType<typeof getAgencyRouteSettings>>;

type Period = '6-months' | '12-months' | 'year-to-date';

const routesKeys = {
  all: ["agencies", "routes"] as const,
  settings: (agencyId: string) => ["agencies", "routes", "settings", agencyId] as const,
  stats: (agencyId: string, period: Period) =>
    ["agencies", "routes", "stats", agencyId, period] as const,
  live: (agencyId: string) => ["agencies", "routes", "live", agencyId] as const,
};

// Hook to get agency route feature settings
export const useAgencyRouteSettings = (agencyId: string) => {
  return useQuery({
    queryKey: routesKeys.settings(agencyId),
    queryFn: () => getAgencyRouteSettings(agencyId),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to toggle agency route feature
export const useUpdateAgencyRouteSettings = (agencyId: string) => {
  const queryClient = useQueryClient();

  return useMutation<AgencyRouteSettings, Error, { enabled: boolean }>({
    mutationFn: ({ enabled }) => updateAgencyRouteSettings(agencyId, enabled),
    onSuccess: async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: routesKeys.settings(agencyId) }),
          queryClient.invalidateQueries({ queryKey: ["agencies", "routes", "stats", agencyId], exact: false }),
          queryClient.invalidateQueries({ queryKey: routesKeys.live(agencyId) }),
        ]);
      } catch (err) {
        // silently ignore invalidation errors
      }
      showToast("Routes settings updated successfully", "success");
    },
    onError: () => {
      showToast("Failed to update routes settings", "error");
    },
  });
};

// Hook to get agency-wide route stats
export const useAgencyRouteStats = (
  agencyId: string,
  period: Period = '6-months',
  startDate?: string,
  endDate?: string
) => {
  const keyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: [...routesKeys.stats(agencyId, period), ...keyExtras] as const,
    queryFn: () => getAgencyRouteStats(agencyId, period, startDate, endDate),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook: read "include owners/admins in stats" flag
export const useStatsInclusion = (agencyId: string) => {
  return useQuery({
    queryKey: ["agencies", "routes", "stats-inclusion", agencyId] as const,
    queryFn: () => getStatsInclusion(agencyId),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook: update the flag + invalidate stats (so all stat boxes refetch)
export const useUpdateStatsInclusion = (agencyId: string) => {
  const queryClient = useQueryClient();
  return useMutation<StatsInclusionSettings, Error, { enabled: boolean }>({
    mutationFn: ({ enabled }) => updateStatsInclusion(agencyId, enabled),
    onSuccess: async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["agencies", "routes", "stats-inclusion", agencyId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["agencies", "routes", "stats", agencyId],
            exact: false,
          }),
          // Invalidate core agency stats (real namespace is "agencies") so
          // Premium/Policies/Agent Count refetch for every period/date combo.
          queryClient.invalidateQueries({
            queryKey: ["agencies", "stats"],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: ["agencies", "sub-agency-stats"],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: ["agencies", "performance-trends"],
            exact: false,
          }),
        ]);
      } catch {
        /* ignore */
      }
      showToast("Stats inclusion updated", "success");
    },
    onError: () => {
      showToast("Failed to update stats inclusion", "error");
    },
  });
};

// Hook to get paginated route history
export const useAgencyRouteHistory = (
  agencyId: string,
  filters: RouteHistoryFilters = {},
) => {
  return useQuery({
    queryKey: [
      "agencies",
      "routes",
      "history",
      agencyId,
      filters.page ?? 1,
      filters.limit ?? 25,
      filters.agentId ?? "",
      filters.status ?? "",
      filters.startDate ?? "",
      filters.endDate ?? "",
    ] as const,
    queryFn: () => getAgencyRouteHistory(agencyId, filters),
    enabled: !!agencyId,
    staleTime: 30 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get live active agents - polls every 20s
export const useAgencyLiveAgents = (agencyId: string) => {
  return useQuery({
    queryKey: routesKeys.live(agencyId),
    queryFn: () => getAgencyLiveAgents(agencyId),
    enabled: !!agencyId,
    refetchInterval: 20000,
    staleTime: 15000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
